import { setProgressBarCurrentStep } from '@avalara/bat-apps-shared/client/actions/progress-bar';
import { getCookie } from '@avalara/bat-apps-shared/helpers/cookies';
import { isMobileDevice } from '@avalara/bat-apps-shared/helpers/index';
import sessionStorage from '@avalara/bat-apps-shared/helpers/sessionStorage';
import { CYBERSOURCE_CANCEL_MESSAGE_TYPE, CYBERSOURCE_SUBMIT_MESSAGE_TYPE } from '@avalara/types/cybersource';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Pages } from '@avalara/types/offers';
import conf from '../conf';
import DefaultPaymentView from '../containers/default-payment-view';
import IframePaymentContainer from '../containers/iframe-payment-container';
import { updateProductAnalytics } from '../lib/side-effects/analytics';
import { getCybersourceFormData } from '../lib/side-effects/cybersource';
import { getSystemErrorMessaging } from '../lib/side-effects/error';
import { addPaidAvataxTier, addReturnsOffer, addRiskAssessmentOffer, purchaseOffer } from '../lib/side-effects/offers';
import { stepNames } from '../lib/side-effects/progress-bar';
import { selectIsNonAdmin, selectState } from '../selectors';
import { setCurrentOfferPage } from '../store/offer';
import { clearSystemError, setNeedToFetchCybersourceData } from '../store/system';
import OfferPaymentView from './offer/forms/payment';
import { FormContainer } from './offer/styled/forms';
import RiskAssessmentPaymentView from './risk-assessment/payment';
import { RelativeDiv } from './styled/skylab/page';

import { addCreditCard } from '../lib/actions';
import analytics from '../lib/analytics';
import { createAvataxPaidAccount, createNetNewSTRAAccount } from '../lib/side-effects/accounts';
import { goToNextOfferStep } from '../lib/side-effects/offer-flow';

import type { CybersourceFormData } from '@avalara/types/cybersource';
import type { InitialAddObligationsOptions } from '@avalara/types/index';

type Props = {
  paymentView?: string;
  isEmbedded?: boolean;
  onSuccess?: () => void;
};

const PaymentFormContainer = styled(FormContainer)`
  height: inherit;
`;

type CybersourceCallback = {
  message?: string;
  paymentMethodId?: string;
  success?: boolean;
  type: string;
};

const defaultFormData: CybersourceFormData = {
  access_key: '',
  amount: '',
  currency: '',
  locale: '',
  profile_id: '',
  reference_number: '',
  signature: '',
  signed_date_time: '',
  signed_field_names: '',
  transaction_type: '',
  transaction_uuid: '',
  unsigned_field_names: '',
  bill_to_forename: '',
  bill_to_surname: '',
  bill_to_address_country: '',
  bill_to_address_line1: '',
  bill_to_address_postal_code: '',
  bill_to_address_city: '',
  bill_to_address_state: '',
  bill_to_company_name: '',
};
const formDataFields = Object.keys(defaultFormData) as Array<keyof CybersourceFormData>;

const CybersourcePaymentPage = ({ paymentView, isEmbedded, onSuccess }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const hiddenForm = useRef<HTMLFormElement>(null);
  const {
    account: { selectedPaymentMethod, isLoggedIn },
    buyFlow: { hideIframe: hideBuyFlowIframe },
    offer: { offerFlow },
    sharedSignup: { signupForm },
    system: { activeFlow, needToFetchCybersourceData },
  } = useSelector(selectState);

  const [formData, setFormData] = useState(defaultFormData);
  const isNonAdmin = useSelector(selectIsNonAdmin);
  const usingExistingPaymentMethod = !!selectedPaymentMethod;
  const hideIframe = usingExistingPaymentMethod || (isLoggedIn && isNonAdmin) || hideBuyFlowIframe;

  useEffect(() => {
    dispatch(setCurrentOfferPage(Pages.Payment));
    dispatch(setProgressBarCurrentStep(stepNames.paymentMethod));
    dispatch(updateProductAnalytics('purchase_start', true));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    if (usingExistingPaymentMethod || !needToFetchCybersourceData) return;
    // NOTE(Sasha): If the state is not 2 letter abbrev or company name is missing
    // from signup form, this call will cause iframe to not render or will open the
    // iframe in a new window
    dispatch(getCybersourceFormData('USD', signupForm, setFormData));
    dispatch(setNeedToFetchCybersourceData(false));
  }, [needToFetchCybersourceData]);

  useEffect(() => {
    // NOTE(Sasha): Need to store activeFlow, since state get cleared out in the callback
    if (activeFlow !== null) sessionStorage.set('activeFlow', activeFlow);
  }, [activeFlow]);

  const onAddObligation = (options: InitialAddObligationsOptions) => {
    const retrievedActiveFlow = sessionStorage.get('activeFlow');
    switch (retrievedActiveFlow) {
      case 'avatax-buy':
        dispatch(addPaidAvataxTier(options));
        break;
      case 'avatax-direct-buy':
        dispatch(createAvataxPaidAccount(options));
        break;
      case 'returns':
        dispatch(addReturnsOffer(options));
        break;
      case 'risk-assessment-net-new':
        dispatch(createNetNewSTRAAccount(options));
        break;
      case 'risk-assessment':
        dispatch(addRiskAssessmentOffer(options));
        break;
      default:
        break;
    }
  };

  const onAddCreditCard = (paymentMethodToken: string) => dispatch(addCreditCard(paymentMethodToken));
  const onHandlePaymentError = (message?: string) =>
    dispatch(
      getSystemErrorMessaging({
        errorCode: 'failedCybersourcePayment',
        msg: message || '',
      }),
    );

  // biome-ignore lint/correctness/useExhaustiveDependencies: do we need deps array here?
  useEffect(() => {
    const callbackListener = (msg: MessageEvent<CybersourceCallback>) => {
      // Bypass cybersource requests if running e2e tests
      if (getCookie('TESTCAFE_E2E')) {
        if (offerFlow) {
          dispatch(goToNextOfferStep({ history }));
        } else {
          onAddObligation({ paymentToken: 'test', history });
        }
        return;
      }
      const { message, paymentMethodId, success, type } = msg.data || {};
      if (type === CYBERSOURCE_CANCEL_MESSAGE_TYPE) {
        dispatch(setNeedToFetchCybersourceData(true));
        // NOTE: This takes you back to the previous page in the flow.
        // Have to use .go(-3) (.go(-1), .go(-2), and .goBack() shows session timeout in the iframe)
        history.go(-3);
      } else if (type === CYBERSOURCE_SUBMIT_MESSAGE_TYPE) {
        if (success) {
          const paymentToken = paymentMethodId || '';

          const options: InitialAddObligationsOptions = {
            history,
            paymentToken,
            handleError: onHandlePaymentError,
          };

          // Clear any error messages
          dispatch(clearSystemError());

          if (paymentView === 'add-credit-card') {
            onAddCreditCard(paymentToken);
            if (onSuccess) onSuccess();
          } else if (paymentView === 'offer' || offerFlow) {
            // ^ returns flows use offer paymentView,
            // so we also need to check if offerFlow is set
            onAddCreditCard(paymentToken);
            if (isEmbedded) {
              dispatch(goToNextOfferStep({ history }));
            } else {
              dispatch(purchaseOffer(history, paymentToken));
            }
          } else {
            onAddObligation(options);
          }
        } else {
          dispatch(setNeedToFetchCybersourceData(true));
          dispatch(getSystemErrorMessaging({ errorCode: 'failedCybersourcePayment', msg: message || '' }));

          const errorData = {
            error: true,
            message,
            // Note: These are resources for troubleshooting the errors.
            errorResources: {
              cybersource:
                'https://developer.cybersource.com/library/documentation/dev_guides/Secure_Acceptance_Hosted_Checkout/html/Topics/Reason_Codes.htm',
            },
          };
          analytics.track('CYBERSOURCE_PAYMENT_SUBMIT_ERROR', errorData);
        }
      }
    };

    window.addEventListener('message', callbackListener);
    return () => window.removeEventListener('message', callbackListener);
  }, []);

  useEffect(() => {
    if (formData.signature) hiddenForm.current?.submit();
  }, [formData.signature]);

  const cybersourcePageEl = document.getElementById('cybersource-page') as HTMLIFrameElement;
  const paymentFormContainerEl = document.getElementById('payment-form-container');
  if (!isMobileDevice() && cybersourcePageEl && paymentFormContainerEl) {
    // NOTE(Sasha): Numbers chosen arbitrarily to adjust inner iframe. Change as necessary
    cybersourcePageEl.width = `${paymentFormContainerEl.offsetWidth - 90}`;
  }

  const getPaymentView = () => {
    switch (paymentView) {
      case 'offer':
        return <OfferPaymentView isEmbedded={isEmbedded} />;
      case 'risk-assessment-net-new':
      case 'risk-assessment':
        return <RiskAssessmentPaymentView />;
      case 'add-credit-card':
        return (
          <RelativeDiv>
            <IframePaymentContainer hideIframe={hideIframe} isCybersource={true} />
          </RelativeDiv>
        );
      default:
        return <DefaultPaymentView />;
    }
  };

  return (
    <>
      <form
        ref={hiddenForm}
        method='post'
        action={`${conf.cybersourcePageHost}/token/create`}
        target='cybersource-page'
      >
        {formDataFields.map((formDataField) => (
          <input
            type='hidden'
            key={formDataField}
            id={formDataField}
            name={formDataField}
            defaultValue={formData[formDataField]}
          />
        ))}
      </form>

      <PaymentFormContainer id='payment-form-container'>{getPaymentView()}</PaymentFormContainer>
    </>
  );
};

export default CybersourcePaymentPage;
